import { utils } from 'ui-common';
import { Provider } from 'react-redux';
import { hashHistory, IndexRoute, LeaveHook, Route, Router } from 'react-router';
import * as _ from 'lodash';
import * as React from 'react';

import * as routingUtils from 'login-utils/routingUtils';
import { fullApplicationTitle } from 'login-constants/titles';
import * as globalWrappers from 'login-utils/globalWrappers';
import { isEnabled } from 'login-utils/featureFlags';
import * as autoRoutingUtils from 'login-utils/autoRoutingUtils';
import * as onboardingRoutingUtils from 'login-utils/onboardingRoutingUtils';
import { updateLastRoute } from 'login-actions/globalActions';
import * as tagManagerUtils from 'login-utils/tagManagerUtils';
import { manuallyLogStateChange, pixelLog } from 'login-utils/pixelUtils';
import * as sessionUtils from 'login-utils/sessionUtils';
import States, { StateName, StatesRich } from 'login-constants/states';
import store from 'login-store';

// Page Containers
import App from './components/App/App';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import LoginWrapper from './components/LoginWrapper/LoginWrapper';
import PaneLayout from './components/Common/PaneLayout/PaneLayout';

// App-Contained Pages
import ActivationExpired from 'login-components/Activation/ActivationExpired/ActivationExpired';
import ChooseApplication from 'login-components/ChooseApplication/ChooseApplication';
import EConsent from 'login-components/EConsent/EConsent';
import LoginLink from 'login-components/LoginLink/LoginLink';
import FastForwardContainer from 'login-components/FastForward/FastForwardContainer/FastForwardContainer';
import InterimSSOTermsOfServiceContainer from 'login-components/TermsOfService/InterimSSOTermsOfServiceContainer';
import LenderActivationOld from 'login-components/Activation/LenderActivationOld';
import LicensingInformation from 'login-components/LicensingInformation/LicensingInformation';
import LoanEstimateExplanation from 'login-components/LoanEstimateExplanation/LoanEstimateExplanation';
import LoanEstimateReceived from 'login-components/LoanEstimate/LoanEstimateReceived';
import LoanEstimateSubtitleLink from 'login-components/LoanEstimate/LoanEstimateSubtitleLink';
import LoanEstimateTermsOfServiceContainer from 'login-components/TermsOfService/LoanEstimateTermsOfServiceContainer';
import LoanHydration from 'login-components/LoanHydration/LoanHydration';
import LoginEmailOnly from 'login-components/Login/LoginEmailOnly';
import LoginPasswordOnly from 'login-components/Login/LoginPasswordOnly';
import MFASetup from 'login-components/MFA/MFASetup';
import MFAValidate from 'login-components/MFA/MFAValidate';
import OAuth from 'login-components/OAuth/OAuth';
import PrivacyPolicy from 'login-components/PrivacyPolicy/PrivacyPolicy';
import ResentActivation from 'login-components/Activation/ResentActivation/ResentActivation';
import ReviewDocumentsCompleted from 'login-components/ReviewDocumentsCompleted/ReviewDocumentsCompleted';
import TermsOfServiceContainer from 'login-components/TermsOfService/TermsOfServiceContainer';
import ViewTermsOfServiceContainer from 'login-components/TermsOfService/ViewTermsOfServiceContainer';
import LottieLoading from 'login-components/LottieLoading/LottieLoading';

// PaneLayout-Contained Pages
import AccountOpening from 'login-components/AccountOpening/AccountOpening';
import BorrowerActivation from 'login-components/Activation/BorrowerActivation';
import BorrowerActivationLeftPane from 'login-components/Activation/BorrowerActivationLeftPane/BorrowerActivationLeftPane';
import ChooseAutoLoanPurpose from 'login-components/ChooseAutoLoanPurpose/ChooseAutoLoanPurpose';
import ChooseAutoPurchaseType from 'login-components/ChooseAutoPurchaseType/ChooseAutoPurchaseType';
import ChooseAutoRefinanceType from 'login-components/ChooseAutoRefinanceType/ChooseAutoRefinanceType';
import ChooseLoanType from 'login-components/ChooseLoanType/ChooseLoanType';
import ChooseResetPassword from 'login-components/ChooseResetPassword/ChooseResetPassword';
import ChooseTimeline from 'login-components/ChooseTimeline/ChooseTimeline';
import CreateAccount from 'login-components/CreateAccount/CreateAccount';
import JourneyChooser from 'login-components/JourneyChooser/JourneyChooserContainer';
import LoanEstimate from 'login-components/LoanEstimate/LoanEstimate';
import Login from 'login-components/Login/Login';
import LoginLeftPane from 'login-components/Login/LoginLeftPane';
import Logout from 'login-components/Logout/Logout';
import Milestones from 'login-components/Milestones/Milestones';
import MilestonesLeftPane from 'login-components/Milestones/MilestonesLeftPane';
import OnboardingForm from 'login-components/OnboardingForm/OnboardingForm';
import ProvideEconsent from 'login-components/ReviewDocuments/ProvideEconsent';
import ProvidePhoneNumber from 'login-components/ProvidePhoneNumber/ProvidePhoneNumber';
import RequestPasswordReset from 'login-components/RequestPasswordReset/RequestPasswordReset';
import RequestDormancyReset from 'login-components/RequestDormancyReset/RequestDormancyReset';
import ReviewDocuments from 'login-components/ReviewDocuments/ReviewDocuments';
import UpfrontRates from 'login-components/UpfrontRates/UpfrontRatesContainer';

// Pane Layout helpers
import { generateLeftPane } from 'login-components/Common/PaneLayout/paneLayoutUtils';

// Standalone Pages
import LoanOfficerPage from 'login-pages/LoanOfficerPage/LoanOfficerPage';

const { bugsnagConfig } = utils;

const redirectToBaseRoute = (nextState, replace) => {
  replace('/');
};

const requireEstimateTermsAccept = (nextState, replace) => {
  if (!_.get(store.getState(), 'terms.acceptedAt')) {
    replace(States.LOAN_ESTIMATE_TERMS);
  }
};

type OnEnter = (nextState: any, replaceState: any) => void;
type Layout = 'default' | 'pane' | 'standalone';

interface AppLayoutRouteConfig {
  component: React.ReactFragment;
  onEnter?: OnEnter;
  layout?: 'default';
}

interface StandaloneRouteConfig {
  component: React.ReactFragment;
  onEnter?: OnEnter;
  layout: 'standalone';
}

interface PaneLayoutRouteConfig {
  components: {
    left: React.ComponentClass<any>;
    right: React.ComponentClass<any>;
  };
  onEnter?: OnEnter;
  layout: 'pane';
}

type RouteConfig = AppLayoutRouteConfig | PaneLayoutRouteConfig | StandaloneRouteConfig;

type RouteConfigs = Partial<Record<StateName, RouteConfig>>;

// TODO UI-2372: just include left pane props in the config & construct SFCs programatically
const routesConfigBorrower: RouteConfigs = {
  SIGNUP: {
    component: () => {
      throw new Error('/signup route is a redirect hub and should not be landed on');
    },
    onEnter: onboardingRoutingUtils.signupOnEnter,
  },
  JOURNEY: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Select a buying stage',
        },
        title: 'Where are you in the mortgage process?',
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: ChooseTimeline,
    },
    onEnter: onboardingRoutingUtils.journeyOnEnter,
    layout: 'pane',
  },
  SHORT_FORM: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Onboarding',
        },
        title: 'Provide a few details so we can find loan options for you',
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: OnboardingForm,
    },
    onEnter: onboardingRoutingUtils.shortFormOnEnter,
    layout: 'pane',
  },
  UPFRONT_RATES: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Upfront Rates',
        },
        loginButton: 'Sign in',
        showBackButton: true,
        subtitle: `For more accurate rates, complete the rest of the application.`,
        title: `Let's check rates based on the info you entered`,
      }),
      right: UpfrontRates,
    },
    onEnter: onboardingRoutingUtils.upfrontRatesOnEnter,
    layout: 'pane',
  },
  MILESTONES: {
    components: {
      left: MilestonesLeftPane,
      right: Milestones,
    },
    onEnter: onboardingRoutingUtils.milestonesOnEnter,
    layout: 'pane',
  },
  CREATE_ACCOUNT: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Sign Up',
        },
        title: fullApplicationTitle,
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: CreateAccount,
    },
    onEnter: routingUtils.goBackIfDeploymentConfigNotEnabled(['allowSignup']),
    layout: 'pane',
  },
  REVIEW_DOCUMENTS_COMPLETED: {
    component: ReviewDocumentsCompleted,
  },
  REQUEST_RESET_PASSWORD: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Enter Email',
        },
        title: `What's your email?`,
        subtitle: `We'll send you a link to get you back into your application.`,
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: RequestPasswordReset,
    },
    layout: 'pane',
  },
  REQUEST_RESET_DORMANCY: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Enter Email',
        },
        title: `Unlock your account`,
        subtitle: `Your account is locked because you haven't logged in within the past 30 days. Enter your email and we'll send you a link to get back into your account.`,
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: RequestDormancyReset,
    },
    layout: 'pane',
  },
  ACTIVATION_PASSWORD_BORROWER: {
    components: {
      left: BorrowerActivationLeftPane,
      right: BorrowerActivation,
    },
    layout: 'pane',
  },
  REVIEW_DOCUMENTS: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Review your documents',
        },
        title: 'Review your documents',
      }),
      right: ReviewDocuments,
    },
    layout: 'pane',
  },
  PROVIDE_ECONSENT: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Confirm Identity',
        },
        title: `Let's confirm your identity`,
      }),
      right: ProvideEconsent,
    },
    layout: 'pane',
  },
  CHOOSE_RESET_PASSWORD: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Choose New Password',
        },
        title: 'Choose your new password',
        loginButton: 'Sign in',
        showBackButton: false,
      }),
      right: ChooseResetPassword,
    },
    layout: 'pane',
  },
  PROVIDE_PHONE_NUMBER: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Provide Your Phone Number',
        },
        title: 'What is the best way to reach you?',
        subtitle: `This is the number we'll use to contact you`,
      }),
      right: ProvidePhoneNumber,
    },
    layout: 'pane',
  },
  FAST_FORWARD: {
    component: FastForwardContainer,
  },
  ACCEPT_TERMS: {
    component: TermsOfServiceContainer,
  },
  JOURNEY_CHOOSER: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Select a buying stage',
        },
        title: 'Where are you in the mortgage process?',
        showBackButton: false,
      }),
      right: JourneyChooser,
    },
    onEnter: onboardingRoutingUtils.journeyChooserOnEnter,
    layout: 'pane',
  },
  VIEW_TERMS: {
    component: ViewTermsOfServiceContainer,
  },
  VIEW_ECONSENT: {
    component: EConsent,
  },
  VIEW_PRIVACY_POLICY: {
    component: PrivacyPolicy,
  },
  RESENT_ACTIVATION: {
    component: ResentActivation,
  },
  ACTIVATION_EXPIRED: {
    component: ActivationExpired,
  },
  LOAN_ESTIMATE_EXPLANATION: {
    component: LoanEstimateExplanation,
  },
  LOAN_ESTIMATE: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Loan Estimate',
        },
        title: 'Provide the following information to obtain a loan estimate',
        subtitle: <LoanEstimateSubtitleLink />,
        showBackButton: false,
        loginButton: 'Sign up',
      }),
      right: LoanEstimate,
    },
    onEnter: requireEstimateTermsAccept,
    layout: 'pane',
  },
  LOAN_ESTIMATE_RECEIVED: {
    component: LoanEstimateReceived,
  },
  LOAN_ESTIMATE_TERMS: {
    component: LoanEstimateTermsOfServiceContainer,
  },
  LOAN_HYDRATION: {
    component: LoanHydration,
    onEnter: routingUtils.redirectIfNoCurrentUser,
  },
  INTERIM_SSO_TERMS: {
    component: InterimSSOTermsOfServiceContainer,
    onEnter: routingUtils.interimSSOOnEnter,
  },
  CHOOSE_LOAN_TYPE: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Choose Loan Type',
        },
        title: 'What are you looking for?',
        showBackButton: false,
        loginButton: 'Sign in',
      }),
      right: ChooseLoanType,
    },
    onEnter: routingUtils.goBackIfDeploymentConfigNotEnabled(['allowSignup']),
    layout: 'pane',
  },
  LOGOUT: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Logout',
        },
        title: 'You have been signed off',
        showBackButton: false,
        loginButton: 'Sign up',
      }),
      right: Logout,
    },
    layout: 'pane',
    onEnter: routingUtils.goBackIfFlagNotEnabled('ssoLogoutPage20170606'),
  },

  ACCOUNT_OPENING: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Choose Account Type',
        },
        title: 'What do you want to apply for?',
        subtitle: 'This should take about 5 minutes. To help speed things along, have your photo ID ready.',
        showBackButton: false,
      }),
      right: AccountOpening,
    },
    onEnter: routingUtils.goBackIfDeploymentConfigNotEnabled(['enableAccountOpening']),
    layout: 'pane',
  },

  AUTO_LOAN: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Choose Auto Loan',
        },
        title: 'What type of auto loan are you looking for?',
        subtitle: '',
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: ChooseAutoLoanPurpose,
    },
    onEnter: autoRoutingUtils.autoOnEnter,
    layout: 'pane',
  },
  AUTO_PURCHASE_TYPE: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Choose Auto Purchase Type',
        },
        title: 'Are you looking for a new or used car?',
        subtitle: 'It’s okay if you haven’t decided yet. Choose one for now, and you can change it later.',
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: ChooseAutoPurchaseType,
    },
    onEnter: autoRoutingUtils.autoOnEnter,
    layout: 'pane',
  },
  AUTO_REFINANCE_TYPE: {
    components: {
      left: generateLeftPane({
        appDocumentTitleProps: {
          title: 'Choose Auto Refinance Type',
        },
        title: 'Which refinance option are you interested in?',
        subtitle: '',
        showBackButton: true,
        loginButton: 'Sign in',
      }),
      right: ChooseAutoRefinanceType,
    },
    onEnter: autoRoutingUtils.autoOnEnter,
    layout: 'pane',
  },

  LICENSING_INFORMATION: {
    component: LicensingInformation,
  },

  CHOOSE_APPLICATION: {
    component: ChooseApplication,
  },
};

const onboardingRoutes = ['JOURNEY', 'SHORT_FORM', 'UPFRONT_RATES', 'MILESTONES', 'CREATE_ACCOUNT', 'CHOOSE_LOAN_TYPE'];

const isDacUserStateCookieEnabled = isEnabled('dacUserStateCookie_20181116');

if (isDacUserStateCookieEnabled) {
  _.forEach(onboardingRoutes, (route: string) =>
    _.extend(_.get(routesConfigBorrower[route], 'components'), {
      top: LoginLink,
    }),
  );
}

const routesConfigStandalone: RouteConfigs = {
  LOAN_OFFICER: {
    component: LoanOfficerPage,
    onEnter: nextState => {
      routingUtils.goBackIfFlagNotEnabled('loanOfficerPage_09182018')(nextState);
      routingUtils.goBackIfDeploymentConfigNotEnabled(['loanOfficerPages.isEnabled'])(nextState);
      routingUtils.goBackIfAllDeploymentConfigsDisabled([
        'allowSignup',
        'loanOfficerPages.enableApplyNowButtonRedirect',
      ])(nextState);
    },
    layout: 'standalone',
  },
  ONE_TAP_LOADING: {
    component: LottieLoading,
    layout: 'standalone',
  },
};

const routesConfigLender: RouteConfigs = {
  LENDER_LOGIN_EMAIL_ONLY: {
    component: LoginEmailOnly,
  },
  LENDER_LOGIN_PASSWORD_ONLY: {
    component: LoginPasswordOnly,
  },
  ACTIVATION_PASSWORD_LENDER: {
    component: LenderActivationOld,
  },
  LENDER_OAUTH: {
    component: OAuth,
  },
  LENDER_MFA_SETUP: {
    component: MFASetup,
  },
  LENDER_MFA_VALIDATE: {
    component: props => <MFAValidate goBackOnReSend={true} {...props} />,
  },
  LENDER_MFA_VERIFY: {
    component: MFAValidate,
  },
};

const allRoutes: RouteConfigs = { ...routesConfigBorrower, ...routesConfigLender, ...routesConfigStandalone };

const routeConfigs = {
  default: {},
  pane: {},
  standalone: {},
};

_.each(allRoutes, (config, key) => {
  if (!config || !key) return;
  const layout = config.layout || 'default';
  routeConfigs[layout][key] = config;
});

const updateBugsnag = (nextState, windowWrapper) => {
  bugsnagConfig.extend({
    page: nextState.name,
    windowWrapper,
  });
};

const onRouteLeave = prevState => {
  store.dispatch(updateLastRoute(prevState.location.pathname));
};

const RouteHOC = function(route) {
  return React.cloneElement(route, {
    onEnter: (nextState, replace) => {
      pixelLog(nextState, store);
      routingUtils.updateLastAttemptedRoute(nextState, store);

      if (_.has(route.props, 'onEnter')) {
        route.props.onEnter(nextState, replace);
      }

      updateBugsnag(route.props, globalWrappers.WindowWrapper);
    },
    onLeave: onRouteLeave,
  });
};

const decorateRoutes = function(routes) {
  return routes.map(route => RouteHOC(route));
};

const getRoutes = function(layout: Layout) {
  return _.map(_.keys(routeConfigs[layout]), key => {
    const route = routeConfigs[layout][key];
    return <Route {..._.assign({}, StatesRich[key], route)} key={key} />;
  });
};

routingUtils.handleInitialRedirect();

// We have an onEnter function defined here and passed directly to the route as opposed to using the
// RouteHOC because we need access to the third arg, callback, and don't want to
// add the complexity of managing that to the RouteHOC.
const loginOnEnter = (nextState, _replace, callback) => {
  // In scenarios where customers expect to only have customers accessing the app through
  // an authentication pattern like SSO, users should never hit this screen. They still do,
  // of course, so we're adding this as a backstop: redirect to a customer-defined URL
  // if a user ever lands on this screen.
  const { ssoLoginRedirectURI, disableBorrowerBlendLogin } = globalWrappers.getWindow().DEPLOYMENT_CONFIG;
  if (ssoLoginRedirectURI && disableBorrowerBlendLogin) {
    manuallyLogStateChange(store, `ssoLoginRedirectURI`);
    globalWrappers.setHref(ssoLoginRedirectURI);
  } else {
    pixelLog(nextState, store);
    callback();
  }
};

const routeConfig = (() => {
  const routes = getRoutes('default');
  const paneRoutes = getRoutes('pane');
  const standaloneRoutes = getRoutes('standalone');
  const catchallRoute = <Route path="*" name="redirect" onEnter={redirectToBaseRoute} key={'*'} />;

  sessionUtils.saveQueryToSession({ signupUrl: window.location.href });
  tagManagerUtils.initializeTagManagersWithMarketingParams();

  return (
    <Provider store={store as any}>
      <Router history={hashHistory}>
        <Route path="/" component={ErrorBoundary}>
          <Route component={LoginWrapper}>
            {/* index route must be first */}
            <Route component={PaneLayout}>
              <IndexRoute
                {...StatesRich.LOGIN}
                components={{
                  left: LoginLeftPane,
                  right: Login,
                }}
                onEnter={loginOnEnter}
                // The react-router typings say that prevState isn't passed as an argument
                // to this function, but they are, so we override them here.
                onLeave={onRouteLeave as LeaveHook}
                key={'LOGIN'}
              />
            </Route>
            <Route component={App}>{decorateRoutes(routes)}</Route>
            <Route component={PaneLayout}>{decorateRoutes(paneRoutes)}</Route>
            <Route>{decorateRoutes(standaloneRoutes)}</Route>
            {/* Catchall Route must be last */}
            <Route component={App}>{decorateRoutes([catchallRoute])}</Route>
          </Route>
        </Route>
      </Router>
    </Provider>
  );
})();

export default routeConfig;
